// PASTE #384: a client-side variety cache so the cart can re-pack through the
// server packer (materializeCart) LIVE, offline, with NO dropped items.
//
// materializeCart needs a full Variety per slug (boxSizes + compatibilityGroup)
// to pack. The static catalog covers most; DDB-only varieties (e.g. Bells of
// Ireland) are primed at boot from fetchVarietiesBatch and at catalog render.
// Seeded lazily with STATIC_VARIETIES so it is never empty. Primed entries
// (client-priced / DDB-only) overwrite the static seed for that slug.

import type { Variety } from "@/lib/catalog/types";
import { STATIC_VARIETIES } from "@/lib/catalog/static-varieties";

const cache = new Map<string, Variety>();
let seeded = false;

function ensureSeed(): void {
  if (seeded) return;
  for (const v of STATIC_VARIETIES) if (!cache.has(v.slug)) cache.set(v.slug, v);
  seeded = true;
}

// Add/refresh varieties (from boot fetchVarietiesBatch or a catalog render).
// These win over the static seed - they carry client-specific pricing + any
// DDB-only variety the static catalog does not have.
export function primeVarietyCache(varieties: Iterable<Variety>): void {
  ensureSeed();
  for (const v of varieties) {
    // #532 cart-save: a price-stripped record (what a signed-out / not-yet-
    // recognised request receives) must never replace a PRICED entry - the
    // cart re-packs through this cache, so one stripped prime turned every
    // line of that variety into $0 and the server refused the save.
    const existing = cache.get(v.slug);
    if (!isPricedVariety(v) && existing && isPricedVariety(existing)) continue;
    cache.set(v.slug, v);
  }
}

// True when the record carries wholesale prices (not price-stripped).
export function isPricedVariety(v: Variety): boolean {
  return v.pricePerStem.length > 0 || v.priceByColor !== undefined;
}

// #532 cart-save: the record to PRICE a cart line from. A price-stripped
// record falls back to the cached priced record for the same slug, so a line
// is never built at $0 when the real price is known. Unchanged otherwise.
export function pricedVarietyFor(v: Variety): Variety {
  if (isPricedVariety(v)) return v;
  ensureSeed();
  const cached = cache.get(v.slug);
  return cached && isPricedVariety(cached) ? cached : v;
}

export function getCartVarietyLookup(): Map<string, Variety> {
  ensureSeed();
  return cache;
}

// True only when EVERY slug is known - the no-drop guard: canonicalization
// runs only when the cache can pack every line, so a line can never vanish.
export function cartCacheHasAll(slugs: string[]): boolean {
  ensureSeed();
  return slugs.every((s) => cache.has(s));
}
